import { useEffect, useState } from 'react';
import type { Session, SupabaseClient } from '@supabase/supabase-js';
import { sbFromEnv } from '../lib/supabase';

/**
 * Returns a Supabase client that is hydrated with the provided session tokens.
 * A new client is created whenever the session tokens change so queries run
 * with the teacher's RLS context. Returns `null` until the session is applied.
 */
type HydratedClientOptions = {
  persistSession?: boolean;
  autoRefreshToken?: boolean;
};

export function useHydratedSupabaseClient<Database = any>(
  session: Session | null | undefined,
  options: HydratedClientOptions = {},
) {
  const url = import.meta.env?.VITE_SUPABASE_URL as string | undefined;
  const key = import.meta.env?.VITE_SUPABASE_ANON_KEY as string | undefined;
  const accessToken = session?.access_token ?? null;
  const refreshToken = session?.refresh_token ?? null;
  const persistSession = options.persistSession ?? false;
  const autoRefreshToken = options.autoRefreshToken ?? false;

  const [client, setClient] = useState<SupabaseClient<Database> | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!url || !key || !accessToken || !refreshToken) {
      setClient(null);
      setReady(false);
      return;
    }

    const next = sbFromEnv<Database>({
      url,
      key,
      options: {
        auth: { persistSession, autoRefreshToken },
      },
    });

    let cancelled = false;
    setReady(false);
    setClient(next as SupabaseClient<Database>);

    (async () => {
      try {
        await next.auth.setSession({ access_token: accessToken, refresh_token: refreshToken });
        if (!cancelled) {
          setReady(true);
        }
      } catch (error) {
        if (!cancelled) {
          console.warn('[useHydratedSupabaseClient] Failed to set session', error);
          setReady(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      setReady(false);
      setClient((current) => (current === next ? null : current));
    };
  }, [url, key, accessToken, refreshToken, persistSession, autoRefreshToken]);

  return ready ? client : null;
}

export default useHydratedSupabaseClient;
